<script setup>
/**
 * ImmersiveCorners
 * Top corner controls for the immersive catalog overlay:
 *   - close X        (top, inset-inline-end -> top-left in RTL)
 *   - theme toggle   (sun/moon, beside the X on its inline-start side)
 * Theme state lives in the useImmersiveTheme singleton (shared with the overlay,
 * which applies the .theme-light class).
 */
import { useImmersiveTheme } from '../../../../core/composables/useImmersiveTheme.js'

defineEmits(['close'])

const { theme, toggleTheme } = useImmersiveTheme()
</script>

<template>
  <div class="imm-corners">
    <!-- Close -->
    <button
      class="imm-corner-btn imm-close"
      type="button"
      aria-label="סגירה"
      @click="$emit('close')"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>

    <!-- Theme toggle -->
    <button
      class="imm-corner-btn imm-theme"
      type="button"
      :aria-label="theme === 'dark' ? 'מצב בהיר' : 'מצב כהה'"
      :title="theme === 'dark' ? 'מצב בהיר' : 'מצב כהה'"
      @click="toggleTheme"
    >
      <Transition name="imm-theme-icon">
        <svg
          v-if="theme === 'dark'"
          key="moon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
        <svg
          v-else
          key="sun"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      </Transition>
    </button>
  </div>
</template>

<style scoped>
.imm-corners {
  display: contents;
}

.imm-corner-btn {
  position: absolute;
  top: calc(12px + env(safe-area-inset-top, 0px));
  z-index: 6;
  width: var(--imm-corner-size, 44px);
  height: var(--imm-corner-size, 44px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--imm-glass-border, rgba(255, 255, 255, 0.18));
  background: var(--imm-glass-bg, rgba(15, 23, 42, 0.55));
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--imm-icon, #ffffff);
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.1s ease;
  -webkit-tap-highlight-color: transparent;
}

.imm-close {
  inset-inline-end: 16px;
}

/* Beside the X on its inline-start side: 16px edge + X width + 10px gap */
.imm-theme {
  inset-inline-end: calc(var(--imm-corner-size, 44px) + 26px);
}

.imm-corner-btn:active {
  background: var(--imm-glass-active, rgba(255, 255, 255, 0.2));
  transform: scale(0.92);
}

/* PC pointer polish: glass buttons lift on hover. */
@media (hover: hover) {
  .imm-corner-btn:hover {
    background: var(--imm-glass-active, rgba(255, 255, 255, 0.16));
    transform: scale(1.06);
  }
}

.imm-close svg {
  width: 22px;
  height: 22px;
}

.imm-theme svg {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 22px;
  height: 22px;
}

.imm-theme-icon-enter-active,
.imm-theme-icon-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.imm-theme-icon-enter-from {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}

.imm-theme-icon-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}

@media (prefers-reduced-motion: reduce) {
  .imm-corner-btn,
  .imm-theme-icon-enter-active,
  .imm-theme-icon-leave-active {
    transition: none;
  }
}
</style>
